import { Icon } from '../../display/Icon';
import { ServiceIcon } from '@/components/patterns/ServiceIcon';

import styles from './ZapCardSteps.module.scss';

export type Step = {
  icon: string;
  id: string;
  label: string;
  name: string;
};

type Props = {
  /**
   * The steps which comprise this Zap template.
   *
   * - `icon` is a URL representing an icon for the service used in this step.
   * - `id` is the ID of the service, e.g. "GoogleFormsAPI".
   * - `label` is the proper name of the trigger/action/search, e.g., "New Response in Spreadsheet".
   * - `name` is the name of the service, e.g., "Google Forms".
   * - `slug` is the service's slug, e.g., "google-forms".
   */
  steps: Array<Step>;
};

type ZapCardStepProps = Step & {
  index: number;
};

const ZapCardStep = (props: ZapCardStepProps) => {
  const isTrigger = props.index === 0;
  const divider = !isTrigger && (
    <div className={styles.divider} data-zds>
      <Icon color="GrayWarm2" name="formAddCircle" size={18} />
    </div>
  );
  return (
    <li className={styles.step} data-zds>
      {divider}
      <ServiceIcon
        aria-hidden={true}
        serviceName={props.name}
        size="medium"
        src={props.icon}
      />
      <div data-zds>
        <p data-zds>{isTrigger ? 'When this happens' : 'Then do this'}</p>
        <p className={styles.stepName} data-zds>
          Step {props.index + 1}: {props.label}
        </p>
      </div>
    </li>
  );
};

/**
 * List of steps (e.g. trigger and actions) for a Zap.
 */
export const ZapCardSteps = (props: Props) => {
  return (
    <ol className={styles.root}>
      {props.steps.map((step, i) => {
        // `key` uses `i` since multiple `steps` might be the same.
        return <ZapCardStep {...step} index={i} key={i} />;
      })}
    </ol>
  );
};
